/**
 * A PDApp as a collectible card (the PDApps window's collection).
 *
 *   Front — a game-library capsule (2:3, its corners the icon tile's own radius): the app's
 *           art fills the whole card (a blurred, enlarged copy of its icon as the
 *           backdrop, the icon itself in front — above every effect, never moved by
 *           one), its name on a solid plate at the foot and, on the line under it, its
 *           KIND's glyph (Personal, org, internal link, external link, public link) with when it was
 *           opened. "New" sits above the name. Every card carries the hourglass glare —
 *           the PDApp mark engraved across it, lit where the light falls — silver; a
 *           FAVORITE's is gold, with a gold rim and a holographic foil.
 *   Click — (or a tap, Enter, Space, a right-click) lifts the card into the middle of
 *           the screen and turns it over: the back holds its details, a star in its
 *           corner (favorite it or not) and its actions — Run first (focused — Enter
 *           twice runs it), then Share (& access), More (the drive's own menu for an app
 *           of this drive), Remove from history. The back is a dialog: focus stays in
 *           it; a click outside, Escape and the phone's Back button close it, and focus
 *           returns to the card. Run bounces the card, then opens the app.
 *   Play  — with a mouse, a ▶ button shows on the card under the pointer: one click runs
 *           the app straight away (the keyboard and touch reach Run on the back).
 *
 * 3D — a mouse lifts and tilts the card under it: a soft light follows the pointer,
 * a sheen sweeps across once, the edges catch the light and the icon moves against
 * the backdrop (parallax); on a phone every card tilts with the phone (`useGyroTilt`,
 * CSS vars on the page). Everything is transforms and opacity; reduced motion turns
 * all of it off.
 *
 * The card decides nothing: its actions are handed in by the window, which runs them
 * through the routes and dialogs it always did.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Building2, Globe, GripVertical, Link2, Network, Play, Star, User, type LucideIcon } from 'lucide-react'
import { parseMiniappIcon, parseMiniappIconRev } from '@papan/types'
import { MiniappIcon, FileContextMenu, useAnchoredPosition, type FileMenuItem } from '@papan/core-ui/ui'
import type { CardColors } from './collection'

/**
 * What kind of PDApp a card is: an app of someone's Personal space, of an organization, or one
 * reached by a link — an internal one (the org the person is in now), an external one (another
 * org's or a Personal space's), or a public link from outside. The glyphs are the ones Papan uses
 * for these everywhere: User / Building2, Link2 (a link), Network (outsiders — Who has access'
 * Outsiders tab), Globe (a Public link).
 */
export type CardKind = 'personal' | 'org' | 'link' | 'external' | 'public'
const KIND_ICON: Record<CardKind, LucideIcon> = { personal: User, org: Building2, link: Link2, external: Network, public: Globe }
/** The kind's glyph — on the card's foot line, and on its back's "From". */
export function KindIcon({ kind, size, className }: { kind: CardKind; size: number; className?: string }) {
  const Icon = KIND_ICON[kind]
  return <Icon size={size} className={className} aria-hidden />
}

export interface CardView {
  id: string
  name: string
  icon: string | null
  /** The folder id an IMAGE icon is fetched by — only for an app of the active org. */
  iconRef: string | null
  /** The line under the name: when it was last opened. */
  subtitle?: string
  /** Where it's from, in full (the tooltip). */
  hint?: string
  /** Personal / org / internal link / external link — its glyph sits on the foot line. */
  kind: CardKind
  /** The kind in words, naming where it's from ("Acme", "Personal", "Public link"…). */
  kindLabel: string
  /** The org it's from — only when that isn't where the person is now. */
  org?: { name: string; personal: boolean }
  /** A favorite: a gold rim, a holographic foil, and its hourglass glare is gold. */
  pinned?: boolean
  /** New to this person since their last visit. */
  isNew?: boolean
  colors: CardColors
}

export interface CardLabels {
  favorite: string
  /** The hover ▶ button ("Run"). */
  play: string
  isNew: string
}

/** The front icon's height for a card `width` px wide; the backdrop's is larger, to cover. */
const iconPxFor = (width: number) => Math.round(width * 0.6)
const backdropPxFor = (width: number) => Math.round(width * 1.9)

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
const hasFinePointer = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(hover: hover) and (pointer: fine)').matches

const colorVars = (c: CardColors, width: number) => ({ '--c1': c.from, '--c2': c.to, '--ink': c.ink, '--card-w': `${width}px` }) as CSSProperties

/** The card's backdrop: its own icon, enlarged and blurred to cover the whole card. */
function Backdrop({ view, width }: { view: CardView; width: number }) {
  return (
    <span className="pdc-bg" aria-hidden>
      <MiniappIcon spec={parseMiniappIcon(view.icon)} name={view.name} size={backdropPxFor(width)} refId={view.iconRef} rev={parseMiniappIconRev(view.icon)} />
    </span>
  )
}

/**
 * The front of a card: full-bleed art, the name on a fade at the foot (its kind's glyph on the
 * line under it), light. Layers, bottom to top: the backdrop, a favorite's hourglass glare, the
 * light, the name plate, the rim — and the app's icon above them all, untouched by any effect.
 * Every card carries the glare (silver; a favorite's is gold).
 */
function CardFront({ view, width, labels }: { view: CardView; width: number; labels: CardLabels }) {
  return (
    <>
      <Backdrop view={view} width={width} />
      <span className="pdc-glare" aria-hidden />
      {view.pinned && <span className="pdc-foil" aria-hidden />}
      <span className="pdc-shine" aria-hidden />
      <span className="pdc-sheen" aria-hidden />
      <span className="pdc-info">
        {view.isNew && <span className="pdc-new" data-testid="apps-card-new">{labels.isNew}</span>}
        <span className="pdc-name">{view.name}</span>
        <span className="pdc-sub" data-testid="apps-tile-kind" data-kind={view.kind}>
          <KindIcon kind={view.kind} size={11} className="pdc-kind" />
          <span className="pdc-sub-text">{view.subtitle ?? view.kindLabel}</span>
        </span>
      </span>
      <span className="pdc-rim" aria-hidden />
      <span className="pdc-art">
        <MiniappIcon spec={parseMiniappIcon(view.icon)} name={view.name} size={iconPxFor(width)} refId={view.iconRef} rev={parseMiniappIconRev(view.icon)} />
      </span>
    </>
  )
}

/**
 * One card in a grid or shelf. `onInspect` flips it (handed the card's element, which
 * the centred view grows out of and returns to).
 */
export function PdappCard({
  view, width, labels, place, bouncing = false, inspecting = false, arranging = false, placeholder = false, dealIndex, onInspect, onPlay, onHover,
}: {
  view: CardView
  /** Where on the page it is drawn (section + id) — one app can be two cards. */
  place?: string
  width: number
  labels: CardLabels
  /** Its app is starting: the card bounces twice (`.apps-launch-bounce`). */
  bouncing?: boolean
  /** Its back is open in the centred view: the card itself steps aside. */
  inspecting?: boolean
  /** The deck is being arranged: the card is moved, not flipped (its grid drags it). */
  arranging?: boolean
  /** It is being dragged: its place shows as an empty slot. */
  placeholder?: boolean
  /** Its place in the deal-in, when the page deals the cards (`.pdc-dealing`). */
  dealIndex?: number
  /** Turn it over (the centred view grows out of the card's element, and focus returns to it). */
  onInspect: (el: HTMLElement) => void
  /** The hover ▶ button: run the app straight away. Absent: no button. */
  onPlay?: () => void
  /** The pointer or focus came to it (the page's light takes its colours). */
  onHover?: () => void
}) {
  // The card is a frame (it tilts) holding two buttons: the whole face turns it over,
  // the ▶ runs the app. A button can't hold another, so neither is the frame.
  const hitRef = useRef<HTMLButtonElement>(null)
  const raf = useRef(0)
  useEffect(() => () => { if (raf.current) cancelAnimationFrame(raf.current) }, [])

  const inspect = () => { if (hitRef.current) onInspect(hitRef.current) }

  // ── Mouse: tilt toward the pointer; glare and foil follow it ──
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (arranging || e.pointerType !== 'mouse' || !hasFinePointer() || prefersReducedMotion()) return
    const el = e.currentTarget
    const r = el.getBoundingClientRect()
    const px = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width))
    const py = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))
    if (raf.current) cancelAnimationFrame(raf.current)
    raf.current = requestAnimationFrame(() => {
      raf.current = 0
      el.classList.add('is-tracking')
      el.style.setProperty('--rx', `${((0.5 - py) * 14).toFixed(2)}deg`)
      el.style.setProperty('--ry', `${((px - 0.5) * 16).toFixed(2)}deg`)
      el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`)
      el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`)
      el.style.setProperty('--px', ((px - 0.5) * 2).toFixed(3))
      el.style.setProperty('--py', ((py - 0.5) * 2).toFixed(3))
    })
  }
  const onPointerLeave = (e: React.PointerEvent<HTMLDivElement>) => {
    if (raf.current) { cancelAnimationFrame(raf.current); raf.current = 0 }
    const el = e.currentTarget
    el.classList.remove('is-tracking')
    for (const p of ['--rx', '--ry', '--mx', '--my', '--px', '--py']) el.style.removeProperty(p)
  }

  return (
    <div
      className={`pdc${view.pinned ? ' pdc-fav' : ''}${bouncing ? ' apps-launch-bounce' : ''}${inspecting ? ' is-inspecting' : ''}${arranging ? ' is-arranging' : ''}${placeholder ? ' is-placeholder' : ''}`}
      style={{ ...colorVars(view.colors, width), ...(dealIndex !== undefined ? { '--deal-i': dealIndex } : {}) } as CSSProperties}
      data-testid="apps-tile"
      data-card-id={view.id}
      data-place={place}
      data-kind={view.kind}
      data-bouncing={bouncing || undefined}
      data-favorite={view.pinned || undefined}
    >
      <div
        className="pdc-card"
        onPointerEnter={onHover}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
        // A card carries nothing on a drag (no icon address either).
        onDragStart={(e) => e.preventDefault()}
      >
        <CardFront view={view} width={width} labels={labels} />
        <button
          ref={hitRef}
          type="button"
          className="pdc-hit"
          title={[view.name, view.hint ?? view.kindLabel].filter(Boolean).join(' — ')}
          aria-label={view.pinned ? `${view.name} (${labels.favorite})` : view.name}
          aria-description={view.kindLabel}
          aria-haspopup="dialog"
          aria-expanded={inspecting}
          // A click, a tap, Enter and Space all turn it over (a button's own keys).
          onClick={(e) => {
            if (arranging) { e.preventDefault(); return }
            inspect()
          }}
          // So do a right-click and the keyboard's menu key, rather than the browser's menu.
          onContextMenu={(e) => {
            e.preventDefault()
            if (!arranging) inspect()
          }}
          onFocus={onHover}
          data-testid="apps-tile-icon"
        />
        {onPlay && !arranging && (
          // A mouse shortcut only: out of the Tab order (the keyboard runs it from the back).
          <button
            type="button"
            className="pdc-play"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); onPlay() }}
            aria-label={`${labels.play}: ${view.name}`}
            title={labels.play}
            data-testid="apps-card-play"
          >
            <Play size={17} />
          </button>
        )}
        {arranging && <span className="pdc-grip" aria-hidden><GripVertical size={14} /></span>}
      </div>
    </div>
  )
}

/** The card under the pointer while it is dragged to a new place in the deck. */
export function CardFloat({ view, width, labels }: { view: CardView; width: number; labels: CardLabels }) {
  return (
    <div className={`pdc pdc-floating${view.pinned ? ' pdc-fav' : ''}`} style={colorVars(view.colors, width)} aria-hidden>
      <div className="pdc-card">
        <CardFront view={view} width={width} labels={labels} />
      </div>
    </div>
  )
}

/** An empty slot in the Favorites deck — "star an app to add it here". */
export function GhostCard({ width, text }: { width: number; text?: string }) {
  return (
    <div className="pdc pdc-ghost" style={{ '--card-w': `${width}px` } as CSSProperties} aria-hidden={!text} data-testid="apps-ghost-card">
      <span className="pdc-ghost-inner">
        <Star size={Math.round(width * 0.16)} />
        {text && <span className="pdc-ghost-text">{text}</span>}
      </span>
    </div>
  )
}

// ── The centred view: the card lifts out and turns over ───────────────────

export interface CardAction {
  id: string
  label: string
  icon: ReactNode
  /** Ignored when `menu` is set (the button opens the menu). */
  onClick: () => void
  primary?: boolean
  danger?: boolean
  testId?: string
  /** A "More" button: the items of the menu it opens, asked for when it opens. */
  menu?: () => FileMenuItem[]
}

export interface CardDetail { label: string; value: string; icon?: ReactNode }

/** The back's star: a favorite or not, turned on and off right there. */
export interface CardFavorite { on: boolean; label: string; onToggle: () => void; testId: string }

/**
 * A back's button that opens a menu (More) — the drive's own glass menu, anchored to it.
 * The arrow keys move through it, Escape and Tab close it (focus back on the button), and
 * the card hears of it (`register`), so its own Escape closes the menu first.
 */
function MenuAction({ action, themeClass, register }: {
  action: CardAction & { menu: () => FileMenuItem[] }
  themeClass: string
  register: (close: (() => void) | null) => void
}) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const registerRef = useRef(register)
  registerRef.current = register
  const close = useCallback((refocus = true) => {
    setOpen(false)
    registerRef.current(null)
    if (refocus) btnRef.current?.focus({ preventScroll: true })
  }, [])
  const toggle = () => {
    if (open) { close(); return }
    setOpen(true)
    registerRef.current(() => close())
  }
  const { pos, exiting, origin } = useAnchoredPosition({
    open, anchorRef: btnRef, floatingRef: menuRef, onDismiss: useCallback(() => close(false), [close]), align: 'start', exitTransitionMs: 160,
  })
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current?.contains(target) || btnRef.current?.contains(target)) return
      close(false)
    }
    document.addEventListener('mousedown', onDoc)
    // Focus the first item once it is placed.
    const t = window.setTimeout(() => menuRef.current?.querySelector<HTMLElement>('button:not([disabled])')?.focus({ preventScroll: true }), 0)
    return () => { document.removeEventListener('mousedown', onDoc); window.clearTimeout(t) }
  }, [open, close])
  useEffect(() => () => registerRef.current(null), [])
  const onMenuKeys = (e: React.KeyboardEvent) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? [])
    const at = items.indexOf(document.activeElement as HTMLElement)
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault(); e.stopPropagation()
      const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length
      items[next]?.focus({ preventScroll: true })
    } else if (e.key === 'Tab') {
      e.preventDefault(); e.stopPropagation()
      close()
    }
  }
  const items = open
    ? action.menu().map((it): FileMenuItem => (it.kind === 'item' ? { ...it, onClick: () => { close(false); it.onClick() } } : it))
    : []
  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle} className="pdc-act" data-testid={action.testId} aria-haspopup="menu" aria-expanded={open}>
        {action.icon}{action.label}
      </button>
      {open && (
        // React events bubble out of the portal to here: the menu's keys are this button's.
        // `display: contents` — the wrapper is no box in the button row (else it takes a gap there,
        // and Share and More shrink while the menu is open).
        <span onKeyDown={onMenuKeys} style={{ display: 'contents' }}>
          <FileContextMenu
            x={pos?.left ?? -9999}
            y={pos?.top ?? -9999}
            exiting={exiting}
            origin={origin}
            items={items}
            menuRef={menuRef}
            className={themeClass}
            testId="apps-card-more-menu"
          />
        </span>
      )}
    </>
  )
}

/**
 * `from`: where the card sits on the page (it grows out of and back into it).
 * `closing`: the window asked it to close — it plays the way back, then `onClosed`.
 * `quick`: close without the way back (an action that leaves the page).
 */
export function CardInspect({ view, from, themeClass, labels, details, actions, favorite, closing, quick, onRequestClose, onClosed }: {
  view: CardView
  from: DOMRect | null
  themeClass: string
  labels: CardLabels
  details: CardDetail[]
  actions: CardAction[]
  /** The star in the back's top-right corner. Absent: the app can't be a favorite here. */
  favorite?: CardFavorite
  closing: boolean
  quick: boolean
  onRequestClose: () => void
  onClosed: () => void
}) {
  // A More menu that is open: the card's Escape closes it first.
  const menuClose = useRef<(() => void) | null>(null)
  const reduced = prefersReducedMotion()
  const [phase, setPhase] = useState<'from' | 'open'>(reduced ? 'open' : 'from')
  const stageRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLDivElement>(null)

  // Size: as big as reads well, and always fits the screen.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768
  const width = Math.round(Math.min(300, vw * 0.82, (vh * 0.8) / 1.5))

  useLayoutEffect(() => {
    if (reduced) return
    let r2 = 0
    const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setPhase('open')) })
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2) }
  }, [reduced])

  // Closing: play the way back (or not), then leave.
  useEffect(() => {
    if (!closing) return
    if (reduced || quick) { onClosed(); return }
    setPhase('from')
    const t = window.setTimeout(onClosed, 460)
    return () => window.clearTimeout(t)
    // `onClosed` is the window's setter; the close is keyed on `closing`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing])

  // Focus the first action once open; keep Tab inside; Escape closes.
  useEffect(() => {
    const t = window.setTimeout(() => backRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus({ preventScroll: true }), reduced ? 0 : 320)
    return () => window.clearTimeout(t)
  }, [reduced])
  // Escape closes it from the first moment — focus reaches the back only once it has
  // turned, so a key pressed before then lands outside the dialog.
  const closeRef = useRef(onRequestClose)
  closeRef.current = onRequestClose
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      if (menuClose.current) menuClose.current()
      else closeRef.current()
    }
    document.addEventListener('keydown', onEsc, true)
    return () => document.removeEventListener('keydown', onEsc, true)
  }, [])
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !backRef.current) return
    const items = Array.from(backRef.current.querySelectorAll<HTMLElement>('button:not([disabled])'))
    if (items.length === 0) return
    const first = items[0]
    const last = items[items.length - 1]
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
  }

  // Where the card starts (and returns to): its place on the page.
  const height = width * 1.5
  const fromTransform = from
    ? `translate(calc(-50% + ${from.left + from.width / 2 - vw / 2}px), calc(-50% + ${from.top + from.height / 2 - vh / 2}px)) scale(${from.width / width})`
    : 'translate(-50%, -50%) scale(0.6)'
  const open = phase === 'open' && !closing
  const primary = actions.filter(a => a.primary)
  const rest = actions.filter(a => !a.primary && !a.danger)
  const danger = actions.filter(a => a.danger)

  return createPortal(
    <div
      className={`pdc-overlay${open ? ' is-open' : ''}${reduced ? ' is-reduced' : ''}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onRequestClose() }}
      data-testid="apps-card-overlay"
    >
      <div
        ref={stageRef}
        className={`pdc-stage ${themeClass}`}
        role="dialog"
        aria-modal="true"
        aria-label={view.name}
        onKeyDown={onKeyDown}
        style={{ ...colorVars(view.colors, width), width, height, transform: open || (reduced && !closing) ? 'translate(-50%, -50%)' : fromTransform }}
      >
        <div className={`pdc-flipper${open || reduced ? ' is-flipped' : ''}${view.pinned ? ' pdc-fav' : ''}`}>
          <div className="pdc-face-side pdc-front" aria-hidden>
            <div className="pdc-card pdc-card-static">
              <CardFront view={view} width={width} labels={labels} />
            </div>
          </div>
          <div ref={backRef} className="pdc-face-side pdc-back" data-testid="apps-card-back">
            <div className="pdc-card pdc-card-static">
              <Backdrop view={view} width={width} />
              <div className="pdc-back-inner">
                {/* (No close button: a click outside, Escape and the phone's Back close it.) */}
                {favorite && (
                  <button
                    type="button"
                    className={`pdc-back-star${favorite.on ? ' is-on' : ''}`}
                    onClick={favorite.onToggle}
                    aria-pressed={favorite.on}
                    aria-label={favorite.label}
                    title={favorite.label}
                    data-testid={favorite.testId}
                  >
                    <Star size={18} />
                  </button>
                )}
                <div className="pdc-back-head">
                  <span className="pdc-back-icon">
                    <MiniappIcon spec={parseMiniappIcon(view.icon)} name={view.name} size={46} refId={view.iconRef} rev={parseMiniappIconRev(view.icon)} />
                  </span>
                  <span className="min-w-0">
                    <span className="pdc-back-name">{view.name}</span>
                  </span>
                </div>
                <dl className="pdc-back-details">
                  {details.map(d => (
                    <div key={d.label}><dt>{d.label}</dt><dd>{d.icon}{d.value}</dd></div>
                  ))}
                </dl>
                <div className="pdc-back-actions">
                  {primary.map((a, i) => (
                    <button key={a.id} type="button" onClick={a.onClick} className="pdc-act pdc-act-primary" data-testid={a.testId} data-autofocus={i === 0 || undefined}>
                      {a.icon}{a.label}
                    </button>
                  ))}
                  {rest.length > 0 && (
                    <div className="pdc-act-row">
                      {rest.map(a => (a.menu
                        ? <MenuAction key={a.id} action={a as CardAction & { menu: () => FileMenuItem[] }} themeClass={themeClass} register={(c) => { menuClose.current = c }} />
                        : (
                          <button key={a.id} type="button" onClick={a.onClick} className="pdc-act" data-testid={a.testId}>
                            {a.icon}{a.label}
                          </button>
                        )))}
                    </div>
                  )}
                  {danger.map(a => (
                    <button key={a.id} type="button" onClick={a.onClick} className="pdc-act pdc-act-danger" data-testid={a.testId}>
                      {a.icon}{a.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
